import { useEffect, useState } from "react";
import {
  Card,
  Typography,
  CardContent,
  Grid,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CheckCircleOutline,
  EventAvailable,
  AccessTime,
} from "@mui/icons-material";

import Colors from "../../../constants/Colors";

interface Props {
  isActive: boolean;
  onCheckout: (
    date: string,
    time: string,
    description: string,
    types: string[],
    otherDesc: string,
    coords: GeolocationCoordinates,
  ) => void;
  checkoutDate: string;
  checkoutTime: string;
  initialDescription?: string;
  initialTypes?: string[];
  initialOtherDesc?: string;
  checkinDate?: string;
  checkinTime?: string;
}

const visitTypesMock = [
  { id: "0", nome: "Reunião" },
  { id: "1", nome: "Venda" },
  { id: "2", nome: "Cliente Novo" },
  { id: "3", nome: "Fechou" },
  { id: "4", nome: "Mudou de Ramo" },
  { id: "5", nome: "Outros" },
];

export const returnApiUrl = () => localStorage.getItem("apiUrl") as string;

const VisitCheckoutCard = ({
  isActive,
  onCheckout,
  checkoutDate,
  checkoutTime,
  initialDescription = "",
  initialTypes = [],
  initialOtherDesc = "",
  checkinDate = "",
  checkinTime = "",
}: Props) => {
  const [description, setDescription] = useState(initialDescription || "");
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialTypes || []);
  const [otherDescription, setOtherDescription] = useState(initialOtherDesc || "");
  const [isFinishing, setIsFinishing] = useState(false);
  const [userCheckoutTime, setUserCheckoutTime] = useState("");
  const [openTimeModal, setOpenTimeModal] = useState(false);
  const [tempTime, setTempTime] = useState("");
  const BAD_ACCURACY_THRESHOLD = 200;

  const API_URL = returnApiUrl();

  // Update states if props change (e.g., when loading from navigate state)
  useEffect(() => {
    if (initialDescription) setDescription(initialDescription);
    if (initialTypes && initialTypes.length > 0) setSelectedTypes(initialTypes);
    if (initialOtherDesc) setOtherDescription(initialOtherDesc);
  }, [initialDescription, initialTypes, initialOtherDesc]);

  const handleTypeChange = (typeId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedTypes((prev) => [...prev, typeId]);
    } else {
      setSelectedTypes((prev) => prev.filter((t) => t !== typeId));
      if (typeId === "5") {
        setOtherDescription("");
      }
    }
  };

  const isOtherSelected = (selectedTypes || []).includes("5");

  // Validate Time
  const checkTimeValid = (timeStr: string) => {
    if (!checkinTime || !timeStr) return false;
    
    const [cHours, cMinutes] = checkinTime.split(":").map(Number);
    const [uHours, uMinutes] = timeStr.split(":").map(Number);
    
    const checkinMinutesTotal = cHours * 60 + cMinutes;
    const checkoutMinutesTotal = uHours * 60 + uMinutes;
    
    if (checkoutMinutesTotal < checkinMinutesTotal) return false;
    if (checkoutMinutesTotal > checkinMinutesTotal + 12 * 60) return false;
    
    return true;
  };

  const isTimeValid = () => checkTimeValid(userCheckoutTime);

  const isFormValid = (description || "").trim().length > 0 && selectedTypes.length > 0 && isTimeValid();

  const handleOpenTimeModal = () => {
    setTempTime(userCheckoutTime || "");
    setOpenTimeModal(true);
  };

  const handleConfirmTime = () => {
    setUserCheckoutTime(tempTime);
    setOpenTimeModal(false);
  };

  const handleFinishVisit = () => {
    setIsFinishing(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const accuracy = position.coords.accuracy;

          if (accuracy > BAD_ACCURACY_THRESHOLD) {
            alert(
              `Sinal de GPS fraco (Precisão: ${Math.round(
                accuracy,
              )}m). Por favor, saia para um local aberto e tente novamente para finalizar a visita com uma localização precisa.`,
            );
            setIsFinishing(false);
            return;
          }

          // Data is always Checkin Date
          const cDate = checkinDate;

          // Time is user typed time + current seconds (or just "00")
          const now = new Date();
          const seconds = String(now.getSeconds()).padStart(2, "0");
          const cTime = `${userCheckoutTime}:${seconds}`;

          await onCheckout(
            cDate,
            cTime,
            description,
            selectedTypes,
            otherDescription,
            position.coords,
          );
          setIsFinishing(false);
        },
        (error) => {
          setIsFinishing(false);
          console.error("Erro ao obter geolocalização no checkout:", error);
          alert(
            "Erro ao obter localização. Verifique as permissões do navegador para finalizar a visita.",
          );
        },
      );
    } else {
      setIsFinishing(false);
      alert("Geolocalização não é suportada por este navegador.");
    }
  };

  return (
    <Grid item xs={12} md={12}>
      <Card sx={{ mb: 2, opacity: isActive ? 1 : 0.5 }}>
        <CardContent>
          <Grid container spacing={2}>
            {/* Header */}
            <Grid item xs={12}>
              <Typography
                sx={{
                  backgroundColor: Colors.titleRowWithBackgroundColor,
                  pl: 1,
                  pt: 0.75,
                  mt: -1,
                  ml: -2,
                  mr: -2,
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
                variant="h6"
                color={Colors.titleWithBackgroudColor}
              >
                <CheckCircleOutline fontSize="inherit" /> Checkout
              </Typography>
            </Grid>

            {!isActive ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" paragraph>
                  O checkout só estará disponível após o início da visita no
                  balcão de Check-in.
                </Typography>
              </Grid>
            ) : (
              <>
                {/* Form Fields */}
                <Grid item xs={12}>
                  <TextField
                    label="Descrição da Visita"
                    placeholder="Relate os pontos principais da visita..."
                    multiline
                    rows={4}
                    fullWidth
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={!!checkoutDate}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Tipos de Visita
                  </Typography>
                  <FormGroup row>
                    {visitTypesMock.map((type) => (
                      <FormControlLabel
                        key={type.id}
                        control={
                          <Checkbox
                            checked={selectedTypes.includes(type.id)}
                            onChange={(e) =>
                              handleTypeChange(type.id, e.target.checked)
                            }
                            disabled={!!checkoutDate}
                          />
                        }
                        label={type.nome}
                      />
                    ))}
                  </FormGroup>
                </Grid>

                {isOtherSelected && (
                  <Grid item xs={12}>
                    <TextField
                      label="Observação (Outros)"
                      placeholder="Especifique o motivo..."
                      fullWidth
                      size="small"
                      value={otherDescription}
                      onChange={(e) => setOtherDescription(e.target.value)}
                      disabled={!!checkoutDate}
                    />
                  </Grid>
                )}

                {/* Final Checkout Date/Time Preview or Finalized */}
                {checkoutDate ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Data (Encerramento)"
                        value={checkoutDate}
                        disabled
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <EventAvailable color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Hora (Encerramento)"
                        value={checkoutTime}
                        disabled
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <AccessTime color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Data (Encerramento)"
                        value={checkinDate}
                        disabled
                        fullWidth
                        size="small"
                        InputProps={{
                          startAdornment: (
                            <EventAvailable color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Hora (Encerramento)"
                        value={userCheckoutTime}
                        onClick={handleOpenTimeModal}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        error={userCheckoutTime.length > 0 && !isTimeValid()}
                        helperText={
                          userCheckoutTime.length > 0 && !isTimeValid()
                            ? "Hora inválida (min: Hora Checkin, max: 12h após)"
                            : "Clique para alterar a hora"
                        }
                        InputProps={{
                          readOnly: true,
                          sx: { cursor: "pointer" },
                          startAdornment: (
                            <AccessTime color="action" sx={{ mr: 1 }} />
                          ),
                        }}
                      />
                    </Grid>
                  </>
                )}

                {/* Checkout Button */}
                {!checkoutDate && (
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      fullWidth
                      color="primary"
                      disabled={!isFormValid || isFinishing}
                      onClick={handleFinishVisit}
                      sx={{ mt: 1, py: 1.5 }}
                    >
                      {isFinishing
                        ? "Capturando localização..."
                        : "Finalizar Visita (Checkout)"}
                    </Button>
                  </Grid>
                )}
                {checkoutDate && (
                  <Grid item xs={12}>
                    <Typography
                      variant="body1"
                      color="success.main"
                      textAlign="center"
                      fontWeight="bold"
                    >
                      Visita Finalizada
                    </Typography>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Dialog 
        open={openTimeModal} 
        onClose={() => setOpenTimeModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Escolha a Hora do Checkout</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Hora"
            type="time"
            value={tempTime}
            onChange={(e) => setTempTime(e.target.value)}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            error={tempTime.length > 0 && !checkTimeValid(tempTime)}
            helperText={
              tempTime.length > 0 && !checkTimeValid(tempTime)
                ? `Inválido. Min: ${checkinTime}, Max: +12h`
                : "Selecione o momento de finalização"
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTimeModal(false)} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmTime} 
            color="primary" 
            variant="contained"
            disabled={tempTime.length === 0 || !checkTimeValid(tempTime)}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default VisitCheckoutCard;
